import { useCallback, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';

import { ProgramTypeEnum } from '../../../constants';
import { GET_LEAGUE_INFO } from '../gql/GET_LEAGUE_INFO';
import { CURRENT_USER_STRIKES } from '../gql/CURRENT_USER_STRIKES';
import { MY_DROPIN_RSVPS_FOR_WEB_IMPL } from '../gql/MY_DROPIN_RSVPS';
import { DROPIN_GAME_INFO } from '../gql/DROPIN_GAME_INFO';
import { TEAM_SCHEDULE_HASURA_IMPL } from '../gql/TEAM_SCHEDULE';
import { CURRENT_USER_SELF_WITHDRAW } from '../gql/CURRENT_USER_SELF_WITHDRAW';
import { CURRENT_USER_CANCEL_DROP_IN } from '../gql/UNRSVP_DROPIN_MUTATION';
import { isWeb } from '../../../utilities';

const usePlayerProgramInfo = ({
  gameId = '',
  leagueId = '',
  programTeamId,
  onCompleted = () => {},
  onError = () => {},
}: {
  gameId?: string;
  leagueId?: string;
  programTeamId?: string;
  onCompleted?: () => void;
  onError?: (arg: string) => unknown;
}) => {
  const [showPopup, setShowPopup] = useState<'confirm' | 'policy' | null>(null);

  const { data: userData, loading: userLoading, error: userError } = useQuery(CURRENT_USER_STRIKES);
  const { currentUser } = userData || {};

  const isMember = Boolean(currentUser?.has_volo_pass);
  const playerStrikes = currentUser?.player_strikes_aggregate?.aggregate?.count || 0;

  const [unrsvpDropInMutation, { loading: unrsvpDropInLoading, error: unrsvpDropInError }] =
    useMutation(CURRENT_USER_CANCEL_DROP_IN);

  const [selfWithdraw, { loading: selfWithdrawLoading, error: selfWithdrawError }] = useMutation(
    CURRENT_USER_SELF_WITHDRAW
  );

  const {
    data: gameData,
    loading: gameLoading,
    error: gameError,
  } = useQuery(DROPIN_GAME_INFO, {
    skip: !gameId || !currentUser?._id,
    fetchPolicy: 'cache-and-network',
    variables: { gameId, userId: currentUser?._id! },
  });
  const dropInSlotId = gameData?.games_by_pk?.drop_in_slots?.[0]?._id;

  const {
    data: leagueData,
    loading: leagueLoading,
    error: leagueError,
  } = useQuery(GET_LEAGUE_INFO, {
    skip: !leagueId,
    fetchPolicy: 'cache-and-network',
    variables: { id: leagueId },
  });

  const {
    data: teamScheduleData,
    loading: teamScheduleLoading,
    error: teamScheduleError,
  } = useQuery(TEAM_SCHEDULE_HASURA_IMPL, {
    skip: !programTeamId,
    fetchPolicy: 'cache-and-network',
    variables: { teamId: programTeamId! },
  });

  const game = gameData?.games_by_pk;
  const dropInStartTime = game?.start_time;
  const dropInEndTime = game?.end_time;

  // returns an array of 1
  const games = teamScheduleData?.teams_by_pk?.leagueByLeague?.games;
  const programGame = games?.[0];

  const gameStartTime = dropInStartTime || programGame?.start_time || '';
  const gameEndTime = dropInEndTime || programGame?.end_time || '';

  const isPast = new Date() > new Date(gameEndTime);
  const hoursBeforeStartTime =
    (new Date(gameStartTime).getTime() - new Date().getTime()) / 3_600_000;

  const league = leagueData?.leagues_by_pk;
  const programType = league?.program_type;

  const adjustedProgramType =
    !programType || programType === ProgramTypeEnum.LEAGUE ? ProgramTypeEnum.DROPIN : programType;

  const onUnrsvpDropIn = useCallback(async () => {
    try {
      if (!dropInSlotId) {
        onError('Cannot un-rsvp, no drop-in slot found');
        return;
      }

      await unrsvpDropInMutation({
        variables: { dropInSlotId },
        onCompleted,
        refetchQueries:
          isWeb && currentUser?._id
            ? [
                {
                  query: MY_DROPIN_RSVPS_FOR_WEB_IMPL,
                  variables: { userId: currentUser._id },
                },
              ]
            : undefined,
      });
    } catch (err) {
      onError(
        err instanceof Error
          ? err.message
          : 'Unknown error occurred while trying to cancel your drop-in spot. Please try again.'
      );
    } finally {
      setShowPopup(null);
    }
  }, [dropInSlotId, unrsvpDropInMutation, currentUser?._id, onError, onCompleted]);

  const onSelfWithdrawDaily = useCallback(
    async (registrantId: string) => {
      try {
        await selfWithdraw({
          variables: {
            registrantId,
          },
          onCompleted,
        });
      } catch (err) {
        onError(
          err instanceof Error
            ? err.message
            : 'Unknown error occurred while trying to cancel your daily spot. Please try again.'
        );
      } finally {
        setShowPopup(null);
      }
    },
    [selfWithdraw, onCompleted, onError]
  );

  const loading =
    leagueLoading ||
    userLoading ||
    gameLoading ||
    unrsvpDropInLoading ||
    selfWithdrawLoading ||
    teamScheduleLoading;

  const error =
    leagueError ||
    userError ||
    gameError ||
    selfWithdrawError ||
    unrsvpDropInError ||
    teamScheduleError;

  return {
    loading,
    error,
    programType: adjustedProgramType,
    isPast,
    hoursBeforeStartTime,
    onUnrsvpDropIn,
    onSelfWithdrawDaily,
    showPopup,
    setShowPopup,
    isMember,
    playerStrikes,
  };
};

export default usePlayerProgramInfo;
